import { parseISO, startOfMonth } from "date-fns";
import { ProcessMonthObjective, ResolvedProcessObjective, UserProcess } from "@/types/user/process-objectives";

// Resolve the carry-forward view for a displayed month.
//
// ProcessMonthObjective stores one row per (process, month). A value entered in
// March stays in effect for April/May/… until a newer row supersedes it — the
// backend already does this at compute time (SFTargetOperation queries
// `month__lte=...order_by("-month").first()`). This function reproduces the same
// resolution for display, so navigating to a month with no exact row still shows
// the value actually in effect (badged as inherited) instead of "no objective".
//
// `rawObjectives` is expected to already be limited to month ≤ displayed month
// (the loader filters with `month_lte`), but we filter defensively here too so
// the function is correct in isolation and easy to unit-test.
export function resolveProcessObjectivesForMonth(
    rawObjectives: ProcessMonthObjective[],
    userProcesses: UserProcess[],
    month: string, // "yyyy-MM"
): ResolvedProcessObjective[] {
    const displayedMonthIso = `${month}-01`;
    const targetMonth = parseISO(displayedMonthIso);
    const isPast = targetMonth < startOfMonth(new Date());

    // Keep rows ≤ displayed month, most recent first, then keep the first seen
    // per process = the value in effect for the displayed month.
    const sorted = [...rawObjectives]
        .filter(obj => parseISO(obj.month) <= targetMonth)
        .sort((a, b) => parseISO(b.month).getTime() - parseISO(a.month).getTime());

    const latestByProcess = new Map<number, ProcessMonthObjective>();
    for (const obj of sorted) {
        if (!latestByProcess.has(obj.user_process)) {
            latestByProcess.set(obj.user_process, obj);
        }
    }

    const resolved: ResolvedProcessObjective[] = [];

    for (const obj of latestByProcess.values()) {
        const objMonth = obj.month.substring(0, 7);
        resolved.push({
            ...obj,
            source: objMonth === month ? "current_month" : "inherited",
            originalMonth: objMonth,
            isPast,
        });
    }

    // Processes that never had any objective ≤ displayed month: synthetic rows so
    // an admin can configure them in one click. Negative ids avoid clashing with
    // real DB rows; they are never edited directly (Create materializes them).
    let syntheticId = -1;
    for (const process of userProcesses) {
        if (latestByProcess.has(process.id)) continue;
        resolved.push({
            id: syntheticId,
            user_process: process.id,
            user_process_detail: process,
            month: displayedMonthIso,
            amount_euros: 0,
            amount_points: 0,
            created_at: "",
            updated_at: "",
            source: "not_configured",
            originalMonth: null,
            isPast,
        });
        syntheticId -= 1;
    }

    return resolved.sort((a, b) => a.user_process_detail.name.localeCompare(b.user_process_detail.name));
}
